import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HandshakeIcon, PaletteIcon, PlusIcon, StoreIcon } from 'lucide-react';
import { pillars } from '../data/pillars';
import { partners } from '../data/partners';
const EASE = [0.23, 1, 0.32, 1] as const;
const themeStyles = {
  sun: {
    panel: 'bg-sun text-ink',
    script: 'text-flamingo',
    body: 'text-ink/80',
    chip: 'bg-ink/10 text-ink'
  },
  flamingo: {
    panel: 'bg-flamingo text-sand',
    script: 'text-sun',
    body: 'text-sand/90',
    chip: 'bg-sand/15 text-sand'
  },
  catracho: {
    panel: 'bg-catracho text-sand',
    script: 'text-sun',
    body: 'text-sand/90',
    chip: 'bg-sand/15 text-sand'
  }
} as const;
const icons = {
  culture: PaletteIcon,
  economic: StoreIcon,
  coalition: HandshakeIcon
} as const;
export function Pillars() {
  const [active, setActive] = useState(0);
  return <section id="pillars" className="bg-dune py-20 md:py-28" data-id="element-299" data-mp="17pbqcfw8mgkd">
      <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-300" data-mp="1cn3eqzxobkjt">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-301" data-mp="116c7smhar2pd">
          <h2 className="font-display text-6xl uppercase leading-[0.9] text-ink md:text-8xl" data-id="element-302" data-mp="1186tw44b10v1">
            Our Three
            <br data-id="element-303" data-mp="sdsd1x44li9t" />
            <span className="text-sunset" data-id="element-304" data-mp="sd8woku4j1xw">Pillars</span>
          </h2>
          <p className="max-w-sm text-lg text-ink/75" data-id="element-305" data-mp="nexn8b7qqtg7">
            Everything we do comes back to culture, opportunity, and the partnerships that make both possible.
            <span className="mt-2 block text-sm font-bold text-ink/60" data-id="element-306" data-mp="1k4ezj918bdqv">Hover or tap a pillar to open it.</span>
          </p>
        </div>

        <div className="mt-14 flex flex-col gap-4 lg:h-[540px] lg:flex-row" data-id="element-307" data-mp="1b49qyvkau9b4">
          {pillars.map((pillar, i) => {
          const s = themeStyles[pillar.theme];
          const Icon = icons[pillar.id];
          const isActive = active === i;
          const panelId = `pillar-panel-${pillar.id}`;
          return <motion.article key={pillar.id} onMouseEnter={() => setActive(i)} animate={{
            flexGrow: isActive ? 3.2 : 1
          }} transition={{
            duration: 0.28,
            ease: EASE
          }} className={`relative flex min-h-[7rem] min-w-0 shrink-0 basis-auto flex-col overflow-hidden rounded-[2rem] lg:min-h-0 lg:shrink lg:basis-0 ${s.panel}`} data-id="element-308" data-mp="3qumn9015za71" data-mpi={i}>
                <h3 data-id="element-309" data-mp="3o9okl8nnp5vz">
                  <button type="button" onClick={() => setActive(i)} onFocus={() => setActive(i)} aria-expanded={isActive} aria-controls={panelId} className="flex w-full items-start justify-between gap-4 p-8 text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-ink/30 md:p-10" data-id="element-310" data-mp="3odcbe01pve70">
                    <span className="flex flex-col gap-6" data-id="element-311" data-mp="2qoivcffeim75">
                      <Icon size={36} strokeWidth={2.2} aria-hidden="true" data-id="element-312" data-mp="2vmav1vmafclw" />
                      <span className={`font-display uppercase leading-none ${isActive ? 'text-4xl md:text-6xl' : 'text-3xl lg:hidden'}`} data-id="element-313" data-mp="2vonde9vyjozh">
                        {pillar.title}
                      </span>
                    </span>
                    <motion.span animate={{
                  rotate: isActive ? 45 : 0
                }} transition={{
                  duration: 0.2,
                  ease: EASE
                }} className="shrink-0 rounded-full border-2 border-current p-1.5" aria-hidden="true" data-id="element-314" data-mp="2gzc6c8etewav">
                      <PlusIcon size={18} strokeWidth={3} data-id="element-315" data-mp="34ppnh3vv3aqo" />
                    </motion.span>
                  </button>
                </h3>

                {!isActive && <span className="pointer-events-none absolute bottom-10 left-10 hidden whitespace-nowrap font-display text-4xl uppercase leading-none [writing-mode:vertical-rl] rotate-180 lg:block" aria-hidden="true" data-id="element-316" data-mp="3dq4dggjrte0i">
                    {pillar.title}
                  </span>}

                <AnimatePresence initial={false} data-id="element-317" data-mp="1bzdymvgkidh6">
                  {isActive && <motion.div id={panelId} key="content" initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.25,
                  delay: 0.1,
                  ease: EASE
                }
              }} exit={{
                opacity: 0,
                transition: {
                  duration: 0.1
                }
              }} className="px-8 pb-8 md:px-10 md:pb-10 lg:mt-auto lg:w-[34rem] lg:max-w-full" data-id="element-318" data-mp="1gt015ut0p0z9">
                      <p className={`font-script text-3xl ${s.script}`} data-id="element-319" data-mp="122j2ukboir6l">{pillar.script}</p>
                      <p className={`mt-3 text-base leading-relaxed md:text-lg ${s.body}`} data-id="element-320" data-mp="29gckzx7smuov">{pillar.body}</p>
                      {pillar.id === 'coalition' && <ul className="mt-5 flex flex-wrap gap-2" data-id="element-321" data-mp="2dg4ovwm8scnw">
                          {partners.map((p, idx) => <motion.li key={p} initial={{
                    opacity: 0,
                    scale: 0.96
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    duration: 0.2,
                    delay: 0.15 + idx * 0.04,
                    ease: EASE
                  }} className={`rounded-full px-3 py-1 text-xs font-bold ${s.chip}`} data-id="element-322" data-mp="1wfbq1xqq77r" data-mpi={idx}>
                              {p}
                            </motion.li>)}
                        </ul>}
                    </motion.div>}
                </AnimatePresence>
              </motion.article>;
        })}
        </div>
      </div>
    </section>;
}